import Base from "./Base";
import Typography from "@mui/material/Typography";
import {InputAdornment, Stack, TextField, useTheme} from "@mui/material";
import Button from "@mui/material/Button";
import {useContext, useState} from "react";
import IconButton from "@mui/material/IconButton";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import {useSnackbar} from "notistack";
import {useHistory} from "react-router-dom";
import {useAuthHeader} from "../../hooks/hooks";
import ConfigContext from "../../context/ConfigContext";
import axios from "axios";
import {z} from "zod";
import useCallDataApi from "../../hooks/data";

const changePasswordSchema = z.object({
    old_password: z.string().min(1, 'A régi jelszó megadása kötelező'),
    new_password: z.string()
        .min(1, 'Az új jelszó megadása kötelező')
        .min(8, 'Az új jelszónak legalább 8 karakternek kell lennie'),
    confirm_password: z.string().min(1, 'Az új jelszó megerősítése kötelező')
}).refine((data) => data.new_password === data.confirm_password, {
    message: 'A jelszavak nem egyeznek',
    path: ['confirm_password']
}).refine((data) => data.old_password !== data.new_password, {
    message: 'Az új jelszónak különböznie kell a régitől',
    path: ['new_password']
});

const ChangePassword = () => {
    const theme = useTheme()
    const {postData: changePassword} = useCallDataApi('change-password')
    const {enqueueSnackbar} = useSnackbar()
    const history = useHistory()
    
    const [data, setData] = useState({
        old_password: '',
        new_password: '',
        confirm_password: ''
    })
    const [passwordVisible, setPasswordVisible] = useState({
        old_password: false,
        new_password: false,
        confirm_password: false
    })
    const [errors, setErrors] = useState({})
    const [loading, setLoading] = useState(false)

    const togglePasswordVisibility = (field) => {
        setPasswordVisible(prev => ({
            ...prev,
            [field]: !prev[field]
        }))
    }

    const validateForm = () => {
        const result = changePasswordSchema.safeParse(data)
        
        if (!result.success) {
            const newErrors = {}
            result.error.issues.forEach((error) => {
                if (error.path.length > 0) {
                    const field = error.path[0]
                    newErrors[field] = error.message
                }
            })
            setErrors(newErrors)
            return false
        }
        
        setErrors({})
        return true
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }
        
        setLoading(true)

        changePassword('', data).then(response => {
            history.push('/')
        }).finally(() => setLoading(false))
    }

    return <Base>
        <div className="login-container">
            <div className="login-card">
                <form onSubmit={handleSubmit}>
                    <Stack spacing={{xs: 2, md: 3, lg: 4}}>
                        <Typography variant="headline6" color={theme.palette.special[500]}>Jelszó változtatás</Typography>
                        <Stack spacing={1} marginBottom={2} marginTop={2}>
                            <Typography variant="caption" color={theme.palette.primary[500]}>Régi jelszó</Typography>
                            <TextField 
                                name="old_password" 
                                type={passwordVisible.old_password ? "text" : "password"} 
                                value={data?.old_password} 
                                onChange={e => {
                                    const newData = {...data, old_password: e.target.value}
                                    setData(newData)
                                    // Clear error for this field when user starts typing
                                    if (errors.old_password) {
                                        const result = changePasswordSchema.safeParse(newData)
                                        const fieldError = result.error?.issues.find(err => err.path[0] === 'old_password')
                                        if (!fieldError) {
                                            setErrors({...errors, old_password: ''})
                                        }
                                    }
                                }}
                                variant='standard'
                                error={!!errors.old_password}
                                helperText={errors.old_password}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => togglePasswordVisibility('old_password')}
                                            edge="end"
                                        >
                                            {passwordVisible.old_password ? <VisibilityOffRoundedIcon /> : <RemoveRedEyeRoundedIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                }}
                            />
                            
                            <Typography variant="caption" color={theme.palette.primary[500]}>Új jelszó</Typography>
                            <TextField 
                                name="new_password" 
                                type={passwordVisible.new_password ? "text" : "password"} 
                                value={data?.new_password} 
                                onChange={e => {
                                    const newData = {...data, new_password: e.target.value}
                                    setData(newData)
                                    // Clear errors for both new_password and confirm_password when new_password changes
                                    if (errors.new_password || errors.confirm_password) {
                                        const result = changePasswordSchema.safeParse(newData)
                                        const newErrors = {...errors}
                                        const newPasswordError = result.error?.issues.find(err => err.path[0] === 'new_password')
                                        const confirmPasswordError = result.error?.issues.find(err => err.path[0] === 'confirm_password')
                                        if (!newPasswordError) {
                                            newErrors.new_password = ''
                                        }
                                        if (!confirmPasswordError) {
                                            newErrors.confirm_password = ''
                                        }
                                        setErrors(newErrors)
                                    }
                                }}
                                variant='standard'
                                error={!!errors.new_password}
                                helperText={errors.new_password}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => togglePasswordVisibility('new_password')}
                                            edge="end"
                                        >
                                            {passwordVisible.new_password ? <VisibilityOffRoundedIcon /> : <RemoveRedEyeRoundedIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                }}
                            />
                            
                            <Typography variant="caption" color={theme.palette.primary[500]}>Új jelszó megerősítése</Typography>
                            <TextField 
                                name="confirm_password" 
                                type={passwordVisible.confirm_password ? "text" : "password"} 
                                value={data?.confirm_password} 
                                onChange={e => {
                                    const newData = {...data, confirm_password: e.target.value}
                                    setData(newData)
                                    // Clear errors for both new_password and confirm_password when confirm_password changes
                                    if (errors.confirm_password || errors.new_password) {
                                        const result = changePasswordSchema.safeParse(newData)
                                        const newErrors = {...errors}
                                        const newPasswordError = result.error?.issues.find(err => err.path[0] === 'new_password')
                                        const confirmPasswordError = result.error?.issues.find(err => err.path[0] === 'confirm_password')
                                        if (!confirmPasswordError) {
                                            newErrors.confirm_password = ''
                                        }
                                        if (!newPasswordError) {
                                            newErrors.new_password = ''
                                        }
                                        setErrors(newErrors)
                                    }
                                }}
                                variant='standard'
                                error={!!errors.confirm_password}
                                helperText={errors.confirm_password}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => togglePasswordVisibility('confirm_password')}
                                            edge="end"
                                        >
                                            {passwordVisible.confirm_password ? <VisibilityOffRoundedIcon /> : <RemoveRedEyeRoundedIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                }}
                            />
                        </Stack>
                        <Button variant="contained" type="submit" disabled={loading}>
                            {loading ? 'Feldolgozás...' : 'Jelszó változtatása'}
                        </Button>
                    </Stack>
                </form>
            </div>
        </div>
    </Base>
}

export default ChangePassword

